import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  ArrowRight, 
  Award, 
  GraduationCap,
  BookOpen
} from "lucide-react";
import { getCarouselSlides, getCarouselSettings } from "../services/apiService";

// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Home = () => {
  // HERO CAROUSEL STATE
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [carouselSettings, setCarouselSettings] = useState({
    transition_type: 'fade',
    transition_duration: 1200,
    auto_play_interval: 4500,
    auto_play_enabled: true,
    easing_function: 'ease-in-out',
    slide_direction: 'right',
    pause_on_hover: true,
    show_navigation: false,
    show_indicators: true,
    blur_intensity: 0,
    brightness_level: 100,
  });

  useEffect(() => {
    const loadSlides = async () => {
      try {
        setLoading(true);
        const [slides, settings] = await Promise.all([
          getCarouselSlides(),
          getCarouselSettings(),
        ]);
        
        if (settings) setCarouselSettings(settings);
        
        if (slides && slides.length > 0) {
          const validSlides = slides.filter(slide => {
            const url = slide.image_url || slide.url;
            return url && !url.startsWith('blob:');
          });
          setHeroSlides(validSlides.length > 0 ? validSlides : slides);
        } else {
          setHeroSlides([{ image_url: "", show_card: false }]);
        }
      } catch (error) {
        console.error('Error loading carousel slides:', error);
        setHeroSlides([{ image_url: "", show_card: false }]);
      } finally {
        setLoading(false);
      }
    };
    loadSlides();

    const handleSettingsUpdate = (event) => setCarouselSettings(event.detail);
    window.addEventListener('carouselSettingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('carouselSettingsUpdated', handleSettingsUpdate);
  }, []);

  useEffect(() => {
    if (heroSlides.length > 0 && carouselSettings.auto_play_enabled) {
      const interval = setInterval(() => {
        if (!isHovering || !carouselSettings.pause_on_hover) {
          setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }
      }, carouselSettings.auto_play_interval || 4500);
      return () => clearInterval(interval);
    }
  }, [heroSlides.length, carouselSettings, isHovering]);

  // Navigation functions (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  // eslint-disable-next-line no-unused-vars
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  const goToSlide = (index) => setCurrentSlide(index);

  const stats = [
    { label: "Success Rate", value: "100%" },
    { label: "Students", value: "17k+" },
    { label: "Experience", value: "17+" },
  ];

  const countries = [
    { name: "USA", code: "us" }, { name: "Australia", code: "au" },{ name: "UK", code: "gb" }, { name: "Canada", code: "ca" },
    { name: "New Zealand", code: "nz" }, { name: "Ireland", code: "ie" }, { name: "Germany", code: "de" }, { name: "France", code: "fr" },
  ];

  const services = [
    { title: "University Admissions", desc: "Strategic shortlisting for Ivy League & Top Tier universities.", icon: <GraduationCap className="w-5 h-5 md:w-6 md:h-6"/> },
    { title: "Visa Documentation", desc: "Error-free filing with 3-tier verification process.", icon: <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6"/> },
    { title: "Scholarship Guidance", desc: "Unlock financial aid and merit-based waivers.", icon: <Award className="w-5 h-5 md:w-6 md:h-6"/> },
    { 
      title: "Test Preparation", 
      desc: "Expert coaching for IELTS, TOEFL & PTE with mock tests.", 
      icon: <BookOpen className="w-5 h-5 md:w-6 md:h-6"/> 
    },
  ];

  const courses = ["IELTS", "TOEFL", "PTE", "OET", "GRE", "GMAT"];

  return (
    <div className="font-sans text-slate-900 bg-white selection:bg-red-100 selection:text-red-900 overflow-x-hidden">

      {/* =========================================
          HERO SECTION
      ========================================= */}
      <section className="relative pt-24 pb-12 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-50 via-white to-white -z-10" />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* LEFT: TEXT & CTAs */}
          <div className="max-w-3xl text-center lg:text-left mx-auto lg:mx-0 mt-3">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-50 border border-slate-200 mb-6 mx-auto lg:mx-0"
            >
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              <span className="text-[10px] md:text-xs font-bold tracking-widest uppercase text-slate-500">
                Admissions Open for Fall 2026
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              initial="hidden"
              animate="show"
              className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-tight text-slate-900 mb-4 md:mb-6"
            >
              Your Future, <br className="hidden md:block" />
              <span className="italic text-slate-800">Designed</span> Globally.
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0 font-medium"
            >
              Stop guessing. We provide precision-engineered roadmaps for IELTS training and overseas admissions.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
            >
              <Link
                to="/book-free-counselling"
                className="group w-full sm:w-auto px-6 py-3.5 bg-red-700 text-white rounded-lg font-bold hover:bg-red-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-700/20"
              >
                Get Free Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/courses"
                className="w-full sm:w-auto px-6 py-3.5 bg-white text-slate-900 border border-slate-200 rounded-lg font-bold hover:border-slate-900 transition-all flex items-center justify-center"
              >
                Explore Courses
              </Link>
            </motion.div>

            {/* Mobile Optimized Stats */}
            <motion.div 
              variants={fadeInUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-slate-100 flex justify-center lg:justify-start gap-8 md:gap-16"
            >
              {stats.map((stat, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div className="text-2xl md:text-3xl font-serif font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT: CAROUSEL (Visible on all devices, optimized aspect ratio) */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative w-full max-w-md mx-auto lg:max-w-full"
          >
            {loading ? (
              <div className="relative aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-slate-100 animate-pulse" />
            ) : heroSlides.length > 0 ? (
              <div 
                className="relative aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-slate-200 group"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {heroSlides.map((slide, index) => {
                  const imageUrl = slide.image_url || slide.url;
                  const safeImageUrl = imageUrl && !imageUrl.startsWith('blob:') ? imageUrl : null;
                  if (!safeImageUrl) return null;
                  
                  const isActive = index === currentSlide;
                  const duration = (carouselSettings.transition_duration || 1200) / 1000;
                  
                  return (
                    <div
                      key={slide.id || index}
                      className="absolute inset-0 w-full h-full"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transition: `opacity ${duration}s ease-in-out`,
                        zIndex: isActive ? 10 : 1
                      }}
                    >
                      <img
                        src={safeImageUrl}
                        alt={`Slide ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Floating Card */}
                      {slide.show_card && (
                        <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-lg border border-white/30 animate-in fade-in slide-in-from-bottom-4 duration-700">
                          <div className="flex items-center gap-3">
                            <div className="bg-green-100 p-2 rounded-full text-green-700 shrink-0">
                              <CheckCircle2 size={20} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-slate-900 truncate">
                                {slide.card_title || 'Visa Approved!'}
                              </p>
                              {slide.card_description && (
                                <p className="text-xs text-slate-600 truncate">{slide.card_description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                  {heroSlides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToSlide(i)}
                      className={`transition-all duration-300 h-1.5 rounded-full ${
                        i === currentSlide ? "w-6 bg-white" : "w-1.5 bg-white/50"
                      }`}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </motion.div>

        </div>
      </section>

      {/* =========================================
          SERVICES SECTION
      ========================================= */}
<section className="py-16 md:py-24 bg-white">
  <div className="max-w-7xl mx-auto px-4 md:px-6">
    
    {/* Main Layout Grid: 1 Col Left (Text), 2 Cols Right (Cards) */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16 items-start">
      
      {/* LEFT COLUMN: Text Content */}
      <div className="lg:col-span-1 lg:sticky lg:top-24">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
          Why choose 6th Sense?
        </h2>
        
        <p className="text-slate-600 text-sm md:text-base leading-relaxed text-justify mb-8">
          6th Sense Academy is Trichy’s trusted study-abroad partner, offering personalised counselling, transparent guidance, and strong global university connections. We support you through applications, SOPs, documentation, visas, scholarships, loans, and accommodation. Our high visa success rate and end-to-end assistance ensure a smooth journey. We also provide IELTS training with expert trainers and score-focused coaching. Choose 6th Sense for a faster, easier path to your dream university abroad.
        </p>

        <Link 
          to="/services" 
          className="inline-flex text-red-700 font-bold hover:underline items-center gap-2 text-sm"
        >
          View All Services <ArrowRight size={16}/>
        </Link>
      </div>

      {/* RIGHT COLUMN: Grid of Cards */}
      <div className="lg:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {services.map((srv, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group p-6 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-red-100 hover:shadow-lg transition-all"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg border border-slate-200 flex items-center justify-center text-red-700 mb-4 shadow-sm group-hover:scale-110 transition-transform duration-300">
                {srv.icon}
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 font-serif">
                {srv.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {srv.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
      
    </div>
  </div>
</section>

      {/* =========================================
          COURSES SECTION
      ========================================= */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-red-500 font-bold tracking-widest uppercase text-xs">Excellence in Training</span>
            <h2 className="text-3xl text-gray-100 md:text-5xl font-serif font-bold mt-2">Target Your Score</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {courses.map((course, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -5 }}
                className="bg-slate-800/50 border border-slate-700 p-4 md:p-6 rounded-xl text-center hover:border-red-500/50 transition-colors cursor-pointer group"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 md:mb-2 group-hover:text-red-500 transition-colors">{course}</div>
                <div className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider font-semibold">Prep</div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-10 md:mt-12 text-center">
             <Link to="/book-ielts-mock-test" className="inline-flex items-center gap-2 text-white border-b border-red-500 pb-1 hover:text-red-400 transition-colors text-sm md:text-base">
               Book a Free Mock Test <ArrowRight size={16} />
             </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          DESTINATIONS (Horizontal Scroll on Mobile)
      ========================================= */}
      <section className="py-16 md:py-24 bg-slate-50 border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="mb-10 md:mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-red-600 mb-2 block">Global Opportunities</span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              Choose Your Destination
            </h2>
          </div>
          
          <div className="flex flex-nowrap md:flex-wrap overflow-x-auto gap-4 md:gap-6 pb-4 md:pb-0 px-2 md:px-0 md:justify-center no-scrollbar snap-x">
            {countries.map((country, i) => (
              <Link 
                key={i} 
                to='/'
                className="snap-start shrink-0 group relative bg-white px-6 py-3 md:px-8 md:py-4 rounded-full border border-slate-200 shadow-sm hover:shadow-xl hover:border-red-200 hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 md:gap-4"
              >
                <div className="relative w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border border-slate-100 shadow-inner group-hover:scale-110 transition-transform">
                  <img 
                    src={`https://flagcdn.com/w80/${country.code}.png`} 
                    alt={country.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-sm md:text-lg font-semibold text-slate-700 group-hover:text-red-700 transition-colors">
                  {country.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          FINAL CTA
      ========================================= */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-red-700 rounded-2xl md:rounded-3xl p-8 md:p-20 text-center text-white relative overflow-hidden shadow-2xl shadow-red-900/30">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4 md:mb-6">Ready to take off?</h2>
              <p className="text-red-100 text-sm md:text-lg mb-8 max-w-2xl mx-auto">
                Your profile is unique. Don't settle for generic advice. Book a 1-on-1 session with our senior counselors today.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-red-800 px-8 py-4 rounded-lg text-base md:text-lg font-bold hover:bg-slate-100 transition-transform hover:scale-105 shadow-lg"
              >
                Book Free Counselling
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
